// frontend/src/site/components/SiteFilterBar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAlgoliaSiteIndex from '../hooks/useAlgoliaSiteIndex';

import { useSite } from '../SiteContext';
import {
  glassClasses,
  inputClasses,
  buttonClasses,
  menuClasses,
  menuHeaderClasses,
  menuItemClasses,
  mutedTextClasses,
  linkClasses,
} from '../theme/siteStyles';

const cx = (...c) => c.filter(Boolean).join(' ');

const PRESETS = [
  50000, 80000, 100000, 120000, 150000, 180000, 200000, 250000, 300000, 350000,
  400000, 500000, 600000, 700000, 800000, 900000, 1000000, 1200000, 1500000,
  2000000,
];

const fmtMoney = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      })
    : '';

const toNumberBR = (s) => {
  const t = String(s ?? '').trim();
  if (!t) return undefined;
  const n = Number(t.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
};

function useDebounced(value, ms = 220) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const TIPOS = [
  { label: 'Casa', value: 'casa' },
  { label: 'Apartamento', value: 'apartamento' },
  { label: 'Apartamento na planta', value: 'apartamento-na-planta' },
  { label: 'Comercial', value: 'comercial' },
  { label: 'Kitnet', value: 'kitnet' },
  { label: 'Terreno / Área', value: 'terreno-area' },
  { label: 'Ponto Comercial', value: 'ponto-comercial' },
  { label: 'Sala Comercial', value: 'sala-comercial' },
  { label: 'Galpão / Barracão', value: 'galpao-barracao' },
  { label: 'Empreendimentos', value: 'empreendimentos' },
  { label: 'Chácara', value: 'chacara' },
  { label: 'Fazenda', value: 'fazenda' },
];

export default function SiteFilterBar({ initial = {} }) {
  const nav = useNavigate();
  const { handle } = useParams();
  const { pathname, search } = useLocation();

  const { theme } = useSite() || {};

  // Algolia (apenas para sugestões do primeiro campo)
  const { index, ready } = useAlgoliaSiteIndex(handle);

  // =========================
  // ✅ 1) LÊ URL -> PREENCHE UI
  // =========================
  const urlState = useMemo(() => {
    const params = new URLSearchParams(search);

    const tipo = (params.get('tipo') || '').trim();
    const dorm = (params.get('dorm') || '').trim();

    const min = params.get('min');
    const max = params.get('max');

    const ref = (params.get('ref') || '').trim();
    const cidade = (params.get('cidade') || '').trim();
    const bairro = (params.get('bairro') || '').trim();
    const local = (params.get('local') || '').trim();

    return {
      tipo,
      dorm,
      min: min ? Number(min) : undefined,
      max: max ? Number(max) : undefined,
      ref,
      cidade,
      bairro,
      local,
    };
  }, [search]);

  // Campo único (onde/ref) + estado derivado
  const [query, setQuery] = useState(
    initial.cidade || initial.bairro || initial.ref || ''
  );
  const [cidade, setCidade] = useState(initial.cidade || '');
  const [bairro, setBairro] = useState(initial.bairro || '');
  const [refCode, setRefCode] = useState(initial.ref || '');
  const [chosenType, setChosenType] = useState(
    initial.ref
      ? 'ref'
      : initial.cidade
        ? 'cidade'
        : initial.bairro
          ? 'bairro'
          : null
  );

  // Demais filtros
  const [tipo, setTipo] = useState(initial.tipo || '');
  const [min, setMin] = useState(
    initial.min ? Number(initial.min).toLocaleString('pt-BR') : ''
  );
  const [max, setMax] = useState(
    initial.max ? Number(initial.max).toLocaleString('pt-BR') : ''
  );
  const [dorm, setDorm] = useState(initial.dorm || '');

  // ✅ evita loop: quando a URL muda por nav(), só sincroniza UI (não submete de volta)
  const syncingRef = useRef(false);

  useEffect(() => {
    syncingRef.current = true;

    // localização/ref: prioridade URL > local > initial
    if (urlState.ref) {
      setChosenType('ref');
      setRefCode(urlState.ref);
      setCidade('');
      setBairro('');
      setQuery(urlState.ref);
    } else if (urlState.cidade) {
      setChosenType('cidade');
      setCidade(urlState.cidade);
      setBairro('');
      setRefCode('');
      setQuery(urlState.cidade);
    } else if (urlState.bairro) {
      setChosenType('bairro');
      setBairro(urlState.bairro);
      setCidade('');
      setRefCode('');
      setQuery(urlState.bairro);
    } else if (urlState.local) {
      setChosenType(null);
      setCidade('');
      setBairro('');
      setRefCode('');
      setQuery(urlState.local);
    } else {
      // sem parâmetros de localização
      setChosenType(null);
      setCidade('');
      setBairro('');
      setRefCode('');
      setQuery('');
    }

    // demais filtros
    setTipo(urlState.tipo || '');
    setDorm(urlState.dorm || '');
    setMin(
      typeof urlState.min === 'number'
        ? urlState.min.toLocaleString('pt-BR')
        : ''
    );
    setMax(
      typeof urlState.max === 'number'
        ? urlState.max.toLocaleString('pt-BR')
        : ''
    );

    // libera depois do tick
    const t = setTimeout(() => {
      syncingRef.current = false;
    }, 0);
    return () => clearTimeout(t);
  }, [urlState]);

  // Sugestões
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const boxRef = useRef(null);
  const debouncedQuery = useDebounced(query, 220);

  useEffect(() => {
    const onClickOut = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  useEffect(() => {
    const term = (debouncedQuery || '').trim();
    if (!ready || !index || term.length < 2) {
      setSuggestions([]);
      return;
    }

    (async () => {
      try {
        const [fc, fb, fr] = await Promise.all([
          index.searchForFacetValues('cidade', term, { maxFacetHits: 6 }),
          index.searchForFacetValues('bairro', term, { maxFacetHits: 6 }),
          index.searchForFacetValues('refCodigo', term, { maxFacetHits: 6 }),
        ]);

        const mapHits = (arr, type) =>
          (arr?.facetHits || []).map((h) => ({ type, value: h.value }));

        const grupos = [];
        const cidades = mapHits(fc, 'cidade');
        if (cidades.length) grupos.push({ header: 'Cidades', items: cidades });

        const bairros = mapHits(fb, 'bairro');
        if (bairros.length) grupos.push({ header: 'Bairros', items: bairros });

        const cods = mapHits(fr, 'ref');
        if (cods.length) grupos.push({ header: 'Códigos', items: cods });

        setSuggestions(grupos);
        setShowSuggest(grupos.some((g) => g.items.length));
      } catch {
        setSuggestions([]);
      }
    })();
  }, [debouncedQuery, index, ready]);

  // Presets de preço
  const [openMin, setOpenMin] = useState(false);
  const [openMax, setOpenMax] = useState(false);
  const minRef = useRef(null);
  const maxRef = useRef(null);

  useEffect(() => {
    const onClickOut = (e) => {
      if (minRef.current && !minRef.current.contains(e.target))
        setOpenMin(false);
      if (maxRef.current && !maxRef.current.contains(e.target))
        setOpenMax(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  // =========================
  // ✅ 2) UI -> ESCREVE URL
  // (somente quando for rota do microsite, pra não quebrar /home)
  // =========================
  const buildSearchParams = () => {
    const params = new URLSearchParams();

    // localização/ref
    if (chosenType === 'ref' && refCode) {
      params.set('ref', refCode);
    } else if (chosenType === 'cidade' && cidade) {
      params.set('cidade', cidade);
    } else if (chosenType === 'bairro' && bairro) {
      params.set('bairro', bairro);
    } else if ((query || '').trim()) {
      params.set('local', query.trim());
    }

    if (tipo) params.set('tipo', tipo);

    const nMin = toNumberBR(min);
    const nMax = toNumberBR(max);
    if (typeof nMin === 'number') params.set('min', String(nMin));
    if (typeof nMax === 'number') params.set('max', String(nMax));

    if (dorm) params.set('dorm', String(dorm));

    return params;
  };

  const submit = () => {
    const params = buildSearchParams();

    nav({
      pathname: `/u/${handle}/imoveis`,
      search: params.toString(),
    });
  };

  // ✅ quando estiver na página /imoveis, atualiza a URL “ao vivo”
  // (tipo, dorm, min/max, etc) sem precisar apertar buscar
  useEffect(() => {
    // só aplica na lista
    if (!pathname.includes('/imoveis')) return;
    if (syncingRef.current) return;

    const params = buildSearchParams();
    const next = params.toString();
    const current = new URLSearchParams(search).toString();

    if (next !== current) {
      nav(
        { pathname: `/u/${handle}/imoveis`, search: next },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tipo,
    dorm,
    min,
    max,
    chosenType,
    refCode,
    cidade,
    bairro,
    query,
    pathname,
  ]);

  const clearAll = () => {
    setQuery('');
    setCidade('');
    setBairro('');
    setRefCode('');
    setChosenType(null);
    setTipo('');
    setMin('');
    setMax('');
    setDorm('');
    nav({ pathname, search: '' });
  };

  const onKeyDownQuery = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const isDark = (theme?.mode || 'light') === 'dark';

  return (
    <div className="w-full">
      {/* ✅ wrapper agora é “glass” do Design System */}
      <div className={cx(glassClasses(theme), 'px-3 py-3')}>
        <div className="grid grid-cols-12 gap-3">
          {/* Onde? ou ID do Imóvel */}
          <div className="col-span-12 sm:col-span-3 relative" ref={boxRef}>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setCidade('');
                setBairro('');
                setRefCode('');
                setChosenType(null);
              }}
              onKeyDown={onKeyDownQuery}
              onFocus={() => setShowSuggest(true)}
              placeholder="Onde? ou ID do Imóvel"
              className={inputClasses(theme)}
            />

            {showSuggest && suggestions.length > 0 && (
              <div
                className={cx('absolute z-20 mt-2 w-full', menuClasses(theme))}
              >
                {suggestions.map((g, gi) => (
                  <div key={`grp-${gi}`} className="py-1">
                    <div className={menuHeaderClasses(theme)}>{g.header}</div>
                    {g.items.map((s, i) => (
                      <button
                        key={`${gi}-${i}-${s.type}-${s.value}`}
                        onClick={() => {
                          if (s.type === 'cidade') {
                            setCidade(s.value);
                            setBairro('');
                            setRefCode('');
                            setChosenType('cidade');
                          } else if (s.type === 'bairro') {
                            setBairro(s.value);
                            setCidade('');
                            setRefCode('');
                            setChosenType('bairro');
                          } else {
                            setRefCode(s.value);
                            setCidade('');
                            setBairro('');
                            setChosenType('ref');
                          }
                          setQuery(s.value);
                          setShowSuggest(false);

                          // ✅ se já estiver na lista, aplica na hora
                          if (pathname.includes('/imoveis')) {
                            const params = buildSearchParams();
                            nav(
                              {
                                pathname: `/u/${handle}/imoveis`,
                                search: params.toString(),
                              },
                              { replace: true }
                            );
                          }
                        }}
                        className={menuItemClasses(theme)}
                        type="button"
                      >
                        <div className="text-sm font-medium">{s.value}</div>
                        <div className={cx('text-[11px]', mutedTextClasses())}>
                          {s.type === 'cidade'
                            ? 'Cidade'
                            : s.type === 'bairro'
                              ? 'Bairro'
                              : 'Código / Ref.'}
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tipo */}
          <div className="col-span-12 sm:col-span-2">
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className={inputClasses(theme)}
            >
              <option value="">Tipo de imóvel</option>
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Nº quartos */}
          <div className="col-span-6 sm:col-span-2">
            <select
              value={dorm}
              onChange={(e) => setDorm(e.target.value)}
              className={inputClasses(theme)}
            >
              <option value="">Nº quartos</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
              <option value="5">5+</option>
            </select>
          </div>

          {/* Valor mín. */}
          <div className="col-span-6 sm:col-span-2 relative" ref={minRef}>
            <input
              value={min}
              onChange={(e) => setMin(e.target.value)}
              onFocus={() => setOpenMin(true)}
              placeholder="Valor mín."
              className={inputClasses(theme)}
            />
            {openMin && (
              <div
                className={cx(
                  'absolute z-20 mt-2 w-full max-h-56 overflow-auto',
                  menuClasses(theme)
                )}
              >
                {PRESETS.map((v) => (
                  <button
                    key={`min-${v}`}
                    onClick={() => {
                      setMin(v.toLocaleString('pt-BR'));
                      setOpenMin(false);
                    }}
                    className={menuItemClasses(theme)}
                    type="button"
                  >
                    {fmtMoney(v)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Valor máx. */}
          <div className="col-span-6 sm:col-span-2 relative" ref={maxRef}>
            <input
              value={max}
              onChange={(e) => setMax(e.target.value)}
              onFocus={() => setOpenMax(true)}
              placeholder="Valor máx."
              className={inputClasses(theme)}
            />
            {openMax && (
              <div
                className={cx(
                  'absolute z-20 mt-2 w-full max-h-56 overflow-auto',
                  menuClasses(theme)
                )}
              >
                {PRESETS.map((v) => (
                  <button
                    key={`max-${v}`}
                    onClick={() => {
                      setMax(v.toLocaleString('pt-BR'));
                      setOpenMax(false);
                    }}
                    className={menuItemClasses(theme)}
                    type="button"
                  >
                    {fmtMoney(v)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Botão Buscar */}
          <div className="col-span-12 sm:col-span-1 flex items-center justify-center">
            <button
              onClick={submit}
              className={cx(
                buttonClasses(theme, 'primary', '!w-12 !px-0'),
                'rounded-full'
              )}
              aria-label="Buscar"
              title="Buscar"
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path d="M20 20L17 17" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* atalhos */}
      <div
        className={cx(
          'mt-4 flex flex-wrap gap-6 justify-center text-sm',
          isDark ? 'text-white/80' : 'text-black/70'
        )}
      >
        {[
          { label: 'Casa', value: 'casa' },
          { label: 'Apartamento', value: 'apartamento' },
          { label: 'Terreno', value: 'terreno-area' },
          { label: 'Sala Comercial', value: 'sala-comercial' },
          { label: 'Empreendimentos', value: 'empreendimentos' },
        ].map((t) => (
          <button
            key={t.value}
            className={cx(linkClasses(theme), 'text-inherit')}
            onClick={() =>
              nav(
                {
                  pathname: `/u/${handle}/imoveis`,
                  search: `?tipo=${encodeURIComponent(t.value)}`,
                },
                { replace: pathname.includes('/imoveis') }
              )
            }
            type="button"
          >
            {t.label}
          </button>
        ))}

        <button
          className={cx(linkClasses(theme), 'opacity-80')}
          onClick={clearAll}
          type="button"
        >
          Limpar filtros
        </button>
      </div>
    </div>
  );
}
